import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import {
  createSettingItem,
  deleteSettingItem,
  fetchSettings,
  selectSettingsGroup,
  updateSettingItem,
} from '@main/features/settings/settingsSlice';
import { UiButton, UiCheckbox, UiInput, UiNativeSelect, UiTextarea } from '@shared/common/ui';

const GROUP = 'notifications';

const CHANNELS = [
  { value: 'telegram', label: 'Telegram' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

const TRIGGERS = [
  { value: 'application_created', label: 'Новая заявка с сайта' },
  { value: 'client_birthday', label: 'День рождения клиента' },
  { value: 'visit_reminder', label: 'Напоминание о занятии' },
  { value: 'payment_due', label: 'Оплата подходит к концу' },
  { value: 'sale_created', label: 'Создана продажа' },
];

const DEFAULT = {
  name: '',
  channel: 'telegram',
  trigger: 'application_created',
  template: 'Новая заявка! {phone} {name} {branch} {direction}',
  enabled: true,
};

export function NotificationsSettings() {
  const dispatch = useDispatch();
  const { items, status, saveError, saveStatus } = useSelector(selectSettingsGroup(GROUP));
  const [editingKey, setEditingKey] = useState(null);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchSettings(GROUP));
  }, [dispatch, status]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: DEFAULT });

  const onSubmit = async (values) => {
    const value = {
      name: values.name.trim(),
      channel: values.channel,
      trigger: values.trigger,
      template: values.template,
      enabled: Boolean(values.enabled),
    };
    const action = editingKey
      ? updateSettingItem({ group: GROUP, key: editingKey, value })
      : createSettingItem({ group: GROUP, value });
    const result = await dispatch(action);
    if (result.meta.requestStatus === 'fulfilled') {
      reset(DEFAULT);
      setEditingKey(null);
    }
  };

  const onEdit = (item) => {
    setEditingKey(item.key);
    reset({
      name: item.value?.name ?? '',
      channel: item.value?.channel ?? 'telegram',
      trigger: item.value?.trigger ?? 'application_created',
      template: item.value?.template ?? '',
      enabled: item.value?.enabled ?? true,
    });
  };

  const onCancel = () => {
    setEditingKey(null);
    reset(DEFAULT);
  };

  const onDelete = async (item) => {
    if (!window.confirm(`Удалить оповещение «${item.value?.name ?? item.key}»?`)) return;
    await dispatch(deleteSettingItem({ group: GROUP, key: item.key }));
  };

  return (
    <div className="form-grid" style={{ alignItems: 'start' }}>
      <div className="form-card">
        <h2>{editingKey ? 'Редактирование оповещения' : 'Новое оповещение'}</h2>
        <p className="muted">
          Плейсхолдеры для новой заявки:{' '}
          <code>{'{phone}'}</code> — телефон, <code>{'{name}'}</code> — имя клиента из заявки,{' '}
          <code>{'{branch}'}</code> — название филиала, <code>{'{direction}'}</code> — направление
          (если указано), <code>{'{date}'}</code> — дата и время заявки.
        </p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <UiInput
            label="Название"
            type="text"
            error={errors.name?.message}
            {...register('name', { required: 'Введите название' })}
          />
          <div className="form-grid" style={{ gap: 12 }}>
            <UiNativeSelect label="Канал" {...register('channel')}>
                {CHANNELS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
            </UiNativeSelect>
            <UiNativeSelect label="Событие" {...register('trigger')}>
                {TRIGGERS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
            </UiNativeSelect>
          </div>
          <UiTextarea
            label="Текст"
            rows={4}
            error={errors.template?.message}
            {...register('template', { required: 'Введите текст' })}
          />
          <UiCheckbox label="Включено" {...register('enabled')} />

          {saveError && <div className="banner banner--danger">{saveError}</div>}

          <div className="form-actions">
            {editingKey && (
              <UiButton variant="ghost" type="button"  onClick={onCancel}>
                Отмена
              </UiButton>
            )}
            <UiButton
              type="submit"
              
              disabled={isSubmitting || saveStatus === 'loading'}
            >
              {editingKey ? 'Сохранить' : 'Добавить'}
            </UiButton>
          </div>
        </form>
      </div>

      <div>
        <h2 style={{ marginTop: 0 }}>Оповещения</h2>
        {items.length === 0 ? (
          <div className="placeholder">Пока нет оповещений</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Канал</th>
                <th>Событие</th>
                <th>Статус</th>
                <th style={{ width: 160 }} />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.key}>
                  <td>{item.value?.name ?? '—'}</td>
                  <td>{CHANNELS.find((c) => c.value === item.value?.channel)?.label ?? '—'}</td>
                  <td>{TRIGGERS.find((t) => t.value === item.value?.trigger)?.label ?? '—'}</td>
                  <td>
                    {item.value?.enabled ? (
                      <span style={{ color: 'var(--color-success)' }}>Вкл</span>
                    ) : (
                      <span className="muted">Выкл</span>
                    )}
                  </td>
                  <td>
                    <div className="row-actions">
                      <UiButton variant="ghost" size="sm"
                        type="button"
                        
                        onClick={() => onEdit(item)}
                      >
                        Изменить
                      </UiButton>
                      <UiButton variant="danger" size="sm"
                        type="button"
                        
                        onClick={() => onDelete(item)}
                      >
                        Удалить
                      </UiButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
